import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { AutocompleteSelect, Button, Toggle } from '../../ui';
import { useMutation, useQuery } from '../../gql/hooks';
import {
  DELETE_MODEL_ALIAS_MUTATION,
  PROBE_PROVIDER_MUTATION,
  PROVIDER_UPSTREAM_MODELS_QUERY,
  UPSERT_MODEL_ALIAS_MUTATION,
  type DeleteModelAliasMutationResult,
  type DeleteModelAliasMutationVariables,
  type ModelAliasRow,
  type ProbeProviderMutationResult,
  type ProbeProviderMutationVariables,
  type ProviderUpstreamModelsResult,
  type UpsertModelAliasMutationResult,
  type UpsertModelAliasMutationVariables,
} from '../../gql/operations';
import { InlineBanner } from '../shared/InlineBanner';

// Model alias editor (2026-08-21, dropdown revision) — each row maps a canonical model
// (what consumers request) to the literal provider model id sent upstream on dispatch.
// Both sides are dropdowns:
//
//   * Canonical side: the enabled catalog + the "*" wildcard ("any canonical model →
//     this target"). One wildcard per provider; a specific alias always wins over it.
//   * Target side: the ids the upstream actually serves (from the probe-populated model
//     list cache), with a pinned "Refresh model list" action that runs a live probe and
//     re-fetches the list — so the operator never has to leave the row to re-sync.
//
// Dropdown changes save immediately (like the enabled toggle); there is no free-text
// entry, which is the point: you can only alias what the box actually serves.

/** Sentinel dropdown value: the wildcard alias (matches any canonical model). */
const WILDCARD = '*';
/** Sentinel dropdown value: re-probe the provider and re-fetch the served-model list. */
const REFRESH = '__refresh__';

export interface ModelAliasEditorProps {
  providerId: string;
  aliases: ModelAliasRow[];
  /** Canonical-model options for the pickers: value = id, label = name (enabled only). */
  canonicalModelOptions: { value: string; label: string }[];
  /** Fired after any successful upsert/delete so the parent can refetch the fleet. */
  onChanged: () => void;
}

export function ModelAliasEditor({
  providerId,
  aliases,
  canonicalModelOptions,
  onChanged,
}: ModelAliasEditorProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Local mirror of the alias list: rows update in place from mutation results so the
  // editor reflects saves immediately, without waiting on the parent's refetch.
  const [rows, setRows] = useState<ModelAliasRow[]>([]);
  const [addCanonical, setAddCanonical] = useState('');
  const [addTarget, setAddTarget] = useState('');

  useEffect(() => {
    setRows(aliases);
  }, [aliases]);

  const upsert = useMutation<UpsertModelAliasMutationResult, UpsertModelAliasMutationVariables>(
    UPSERT_MODEL_ALIAS_MUTATION,
  );
  const remove = useMutation<DeleteModelAliasMutationResult, DeleteModelAliasMutationVariables>(
    DELETE_MODEL_ALIAS_MUTATION,
  );
  const probe = useMutation<ProbeProviderMutationResult, ProbeProviderMutationVariables>(
    PROBE_PROVIDER_MUTATION,
  );

  // Served-model ids from the probe-populated cache (no live probe on read).
  const upstream = useQuery<ProviderUpstreamModelsResult>(PROVIDER_UPSTREAM_MODELS_QUERY, {
    id: providerId,
  });
  const upstreamIds = useMemo(
    () => (upstream.data?.providerUpstreamModels.models ?? []).map((m) => m.id),
    [upstream.data],
  );

  // "Refresh model list" — a live probe re-populates the model list cache, then re-read it.
  const refreshUpstreamList = async () => {
    try {
      await probe.mutate({ id: providerId });
      upstream.refetch();
    } catch {
      // A failed probe leaves the cached list as-is; the probe UI on the form surfaces why.
    }
  };

  // Target dropdown options: the pinned refresh action + served ids + (when the current
  // value predates the served list — e.g. a stale seed alias) the current value itself,
  // so the AutocompleteSelect can still display it.
  const targetOptions = (current: string | null) => {
    const base = upstreamIds.map((id) => ({ value: id, label: id }));
    if (current && !upstreamIds.includes(current)) {
      base.unshift({ value: current, label: `${current} — not served upstream` });
    }
    return [
      {
        value: REFRESH,
        label: probe.loading ? 'Refreshing…' : '↻ Refresh model list',
      },
      ...base,
    ];
  };

  // Canonical dropdown options for one row (or the add form): the wildcard (unless another
  // row already claims it) + enabled canonicals not aliased on other rows. `self` keeps the
  // row's own current selection present.
  const canonicalOptions = (self: ModelAliasRow | null) => {
    const takenNames = new Set(
      rows.filter((r) => r.id !== self?.id && !r.wildcard).map((r) => r.canonicalModel),
    );
    const wildcardTaken = rows.some((r) => r.wildcard && r.id !== self?.id);

    const options = canonicalModelOptions
      .filter((o) => !takenNames.has(o.label))
      .map((o) => ({ value: o.label, label: o.label }));

    // A row's current canonical may be disabled in the catalog (absent from the options) —
    // keep it selectable so the dropdown can render the selection.
    if (self && !self.wildcard && !options.some((o) => o.value === self.canonicalModel)) {
      options.unshift({ value: self.canonicalModel, label: self.canonicalModel });
    }

    if (!wildcardTaken) {
      options.unshift({ value: WILDCARD, label: '* — any canonical model' });
    }

    return options;
  };

  const applyResult = (saved: ModelAliasRow) => {
    setRows((prev) => {
      const known = prev.some((row) => row.id === saved.id);
      if (!known) return [saved, ...prev];
      return prev.map((row) => (row.id === saved.id ? saved : row));
    });
    onChanged();
  };

  const saveRow = async (
    row: ModelAliasRow,
    changes: { canonical?: string; providerModel?: string; enabled?: boolean },
  ) => {
    const input: UpsertModelAliasMutationVariables['input'] = {
      id: row.id,
      providerModel: changes.providerModel ?? row.providerModel,
      enabled: changes.enabled ?? row.enabled,
    };

    // A canonical-side change carries the full shape: wildcard on/off + (when concrete)
    // the canonical name the context resolves to its catalog row.
    if (changes.canonical !== undefined) {
      input.wildcard = changes.canonical === WILDCARD;
      if (changes.canonical !== WILDCARD) input.canonicalModel = changes.canonical;
    }

    try {
      const result = await upsert.mutate({ input });
      applyResult(result.upsertModelAlias);
    } catch {
      // Surfaced via upsert.error banner; the row keeps its previous value.
    }
  };

  const deleteRow = async (row: ModelAliasRow) => {
    try {
      await remove.mutate({ id: row.id });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      onChanged();
    } catch {
      // Surfaced via remove.error banner.
    }
  };

  const addAlias = async () => {
    const input: UpsertModelAliasMutationVariables['input'] = {
      providerId,
      providerModel: addTarget,
      wildcard: addCanonical === WILDCARD,
    };
    if (addCanonical !== WILDCARD) input.canonicalModel = addCanonical;

    try {
      const result = await upsert.mutate({ input });
      applyResult(result.upsertModelAlias);
      setAddCanonical('');
      setAddTarget('');
    } catch {
      // Surfaced via upsert.error banner; the add form keeps its values for retry.
    }
  };

  /** Route a target-dropdown selection: the refresh action vs an actual model id. */
  const onTargetSelect = (value: string, commit: (id: string) => void) => {
    if (value === REFRESH) {
      void refreshUpstreamList();
      return;
    }
    commit(value);
  };

  const mutationError = upsert.error ?? remove.error ?? null;

  return (
    <div data-testid="model-alias-editor" className="space-y-3">
      {mutationError && (
        <InlineBanner tone="error" testId="alias-mutation-error">
          That alias change couldn’t be saved: {mutationError.message}
        </InlineBanner>
      )}

      {rows.length === 0 && (
        <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
          No aliases yet — this provider is not eligible for any canonical model. Add one
          below, or use <strong>Refresh models</strong> to import the upstream catalog.
        </p>
      )}

      <ul className="space-y-2">
        {rows.map((row) => {
          const name = row.wildcard ? WILDCARD : row.canonicalModel;
          return (
            <li
              key={row.id}
              data-testid={`alias-row-${name}`}
              className={`grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1.2fr)_auto] items-center gap-2 rounded-xl border p-2.5 ${
                isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-white'
              }`}
            >
              <AutocompleteSelect
                label={`Canonical model for alias ${name}`}
                value={name}
                options={canonicalOptions(row)}
                onChange={(next) => {
                  if (next !== name) void saveRow(row, { canonical: next });
                }}
              />

              <span aria-hidden="true" className={isDark ? 'text-white/30' : 'text-slate-400'}>
                →
              </span>

              <AutocompleteSelect
                label={`Provider model for ${name}`}
                value={row.providerModel}
                options={targetOptions(row.providerModel)}
                onChange={(next) =>
                  onTargetSelect(next, (id) => {
                    if (id !== row.providerModel) void saveRow(row, { providerModel: id });
                  })
                }
              />

              <div className="flex items-center gap-1.5">
                <Toggle
                  checked={row.enabled}
                  label={`Alias ${name} enabled`}
                  loading={upsert.loading}
                  onChange={(next) => void saveRow(row, { enabled: next })}
                />
                <Button
                  variant="destructive"
                  size="sm"
                  loading={remove.loading}
                  onClick={() => void deleteRow(row)}
                  leftIcon={<Trash2 className="h-3.5 w-3.5" aria-hidden="true" />}
                  aria-label={`Delete alias ${name}`}
                >
                  Delete
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Add-alias row: canonical (or wildcard) + served-model target. */}
      <div
        className={`grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1.2fr)_auto] items-center gap-2 rounded-xl border border-dashed p-2.5 ${
          isDark ? 'border-white/[0.12]' : 'border-slate-300'
        }`}
      >
        <AutocompleteSelect
          label="New alias canonical model"
          value={addCanonical}
          options={canonicalOptions(null)}
          onChange={setAddCanonical}
          placeholder="Canonical model…"
        />
        <span aria-hidden="true" className={isDark ? 'text-white/30' : 'text-slate-400'}>
          →
        </span>
        <AutocompleteSelect
          label="New alias provider model"
          value={addTarget}
          options={targetOptions(addTarget || null)}
          onChange={(next) => onTargetSelect(next, setAddTarget)}
          placeholder={upstreamIds.length === 0 ? 'Refresh the model list…' : 'Target model…'}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={addCanonical === '' || addTarget === ''}
          loading={upsert.loading}
          onClick={() => void addAlias()}
          leftIcon={<Plus className="h-3.5 w-3.5" aria-hidden="true" />}
        >
          Add alias
        </Button>
      </div>
    </div>
  );
}
